import {
  ANALYTIC_TABLE_TITLE,
  type TableInsight,
  TableInsightAction,
  TitleWithFilters,
  type TitleWithFiltersProps,
} from "@monthly/ui";
import {
  CompareArrowsRounded,
  Fullscreen,
  TableChartOutlined,
  Timeline,
  TimelineRounded,
  TrendingUp,
} from "@mui/icons-material";
import { Box, Card, Stack, styled, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useImperativeHandle, useRef, useState } from "react";

import type { TwoColumnLayoutV2Handle } from "~/components/charts/downloadSection";
import { IconButton } from "../icon-button/IconButton";
import { FullScreenDialog } from "./FullScreenDialog";

export const TwoColumnLayout = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "showTable",
})<{ showTable: boolean }>(({ showTable }) => ({
  display: "grid",
  gridTemplateColumns: showTable ? "2fr 1fr" : "1fr",
  gap: 16,
}));

export const SectionCard = styled(Card)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  borderRadius: 12,
  boxShadow: theme.shadows[4],
  padding: 16,
  marginBottom: 0,
  transition: "all 0.2s ease-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[5],
  },
}));

export interface ScrollableTableProps {
  children: ReactNode;
  maxRows?: number;
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { TwoColumnLayoutV2Props } from '@monthly/ui' */
export interface TwoColumnLayoutV2Props {
  period: string;
  title: string;
  tableDescription: string;
  subtitle: string;
  currency: string;
  units: string;
  verticalFlowNetData?: number[];
  onViewChange?: (view: "primary" | "vertical") => void;
  onTimelineClick?: () => void;
  onTrendingClick?: () => void;
  onDialogDownload?: () => void;
  currentView?: "primary" | "vertical";
  showActionButtons?: boolean;
  hasTimeline?: boolean;
  hasTrendingUp?: boolean;
  hasFullscreen?: boolean;
  useFilters?: TitleWithFiltersProps["useFilters"];
  showTable?: boolean;
  /** Mara insights of the table. The Ask Mara icon is shown only when provided. */
  tableInsight?: TableInsight;
  children: {
    chart: ReactNode;
    table: ReactNode;
  };
  ref: React.Ref<TwoColumnLayoutV2Handle>;
}

export const DEFAULT_MAX_ROWS = 7.5;
export const TABLE_ROW_HEIGHT = 40;

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { ScrollableTable } from '@monthly/ui' */
export function ScrollableTable({
  children,
  maxRows = DEFAULT_MAX_ROWS,
}: ScrollableTableProps) {
  return (
    <Stack
      sx={{
        mt: 2,
        maxHeight: `calc(${maxRows} * ${TABLE_ROW_HEIGHT}px)`,
        overflow: "auto",
      }}
    >
      {children}
    </Stack>
  );
}

/** @deprecated Import from `@monthly/ui` instead
 * @example
 * import { TwoColumnLayoutV2 } from '@monthly/ui' */
export function TwoColumnLayoutV2({
  period,
  title,
  tableDescription,
  subtitle,
  currency,
  units,
  onViewChange,
  onTimelineClick,
  onTrendingClick,
  currentView = "primary",
  showActionButtons = true,
  children,
  ref,
  hasTimeline = true,
  hasTrendingUp = true,
  hasFullscreen = true,
  showTable = true,
  onDialogDownload,
  useFilters,
  tableInsight,
}: TwoColumnLayoutV2Props) {
  const [isFullscreenIsOpen, setIsFullscreenOpen] = useState(false);

  const handleTimelineClick = () => {
    if (onTimelineClick) onTimelineClick();
    else onViewChange?.("primary");
  };

  const handleTrendingClick = () => {
    if (onTrendingClick) onTrendingClick();
    else onViewChange?.("vertical");
  };

  const handleOpenFullscreen = () => setIsFullscreenOpen(true);
  const handleCloseFullscreen = () => setIsFullscreenOpen(false);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartAndTableRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    getChartRef: () => chartRef.current,
    getChartAndTableRef: () => chartAndTableRef.current,
  }));

  return (
    <Stack gap={2}>
      <FullScreenDialog
        currency={currency}
        title={title}
        subtitle={subtitle}
        period={period}
        children={children}
        onDownload={onDialogDownload}
        isFullscreenIsOpen={isFullscreenIsOpen}
        handleCloseFullscreen={handleCloseFullscreen}
        units={units}
      />
      <TwoColumnLayout ref={chartAndTableRef} showTable={showTable}>
        {/* Left Card - Chart */}
        <SectionCard ref={chartRef}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              width: "100%",
              gap: { xs: 2, sm: 0 },
            }}
          >
            {/* Title Section */}
            <Stack spacing={1} sx={{ width: "100%", maxWidth: { sm: 540 } }}>
              <TitleWithFilters
                icon={
                  subtitle === "Comparables" ? (
                    <CompareArrowsRounded />
                  ) : (
                    <TimelineRounded />
                  )
                }
                title={title}
                subtitle={subtitle}
                useFilters={useFilters}
              />
            </Stack>

            {/* Action Buttons */}
            {showActionButtons && (
              <Stack
                direction="row"
                spacing={{ xs: 1.5, sm: 2 }}
                alignSelf="flex-start"
              >
                {hasTimeline && (
                  <IconButton
                    onClick={handleTimelineClick}
                    isSelected={currentView === "primary"}
                    tooltip="Tendencia"
                  >
                    <Timeline fontSize="small" />
                  </IconButton>
                )}

                {hasTrendingUp && (
                  <IconButton
                    onClick={handleTrendingClick}
                    isSelected={currentView === "vertical"}
                    tooltip="Crecimiento"
                  >
                    <TrendingUp fontSize="small" />
                  </IconButton>
                )}

                {hasFullscreen && (
                  <IconButton
                    onClick={handleOpenFullscreen}
                    isSelected={isFullscreenIsOpen}
                    tooltip="Fullscreen"
                  >
                    <Fullscreen fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            )}
          </Box>

          {children.chart}
        </SectionCard>

        {/* Right Card - Table */}
        {showTable && (
          <SectionCard>
            <Stack gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <TableChartOutlined
                    sx={{ color: "primary.light", fontSize: 24 }}
                  />
                  <Typography variant="h5" color="primary.main">
                    {ANALYTIC_TABLE_TITLE}
                  </Typography>
                </Stack>
                {tableInsight && (
                  <TableInsightAction
                    title={ANALYTIC_TABLE_TITLE}
                    {...tableInsight}
                  />
                )}
              </Stack>

              <Typography variant="overline" color="text.primary">
                {tableDescription}
              </Typography>
            </Stack>

            <ScrollableTable>{children.table}</ScrollableTable>
          </SectionCard>
        )}
      </TwoColumnLayout>
    </Stack>
  );
}
